// Map zones - places where a pickup or drop-off needs different handling from
// ordinary kerbside. Two kinds:
//
//   no_waiting  Nairobi's CBD streets and the airport frontage are patrolled
//               hard enough that a stationary vehicle is moved on within
//               seconds. When a pickup lands in one, the driver has to keep
//               circling, the rider watches them drive past, and somebody
//               cancels. So we say it out loud: the map shows a "Pickup in
//               no-waiting zone" banner and tells the rider to be at the kerb.
//
//   parking     Malls and big supermarkets charge the DRIVER at the barrier. On
//               a KES 300 trip a KES 50 ticket is a fifth of the fare out of
//               their pocket, which is why it gets argued about at the gate.
//               The rider decides at booking instead: dropped inside, or dropped
//               at the gate.
//
// The geometry is fetched once and cached, then evaluated HERE rather than on
// the server, because the question has to be answered the instant a pin moves -
// before any ride exists to ask about. The containment test is a line-for-line
// mirror of the backend's (backend/src/utils/zones.ts), so what the rider is
// shown while booking and what is frozen onto their ride always agree.
//
// The amount of any parking fee is NOT taken from here when booking - the server
// re-derives it from its own copy of the zone (see parkingFeeFor). This copy
// exists to show the rider the figure before they commit.

export type ZoneType = 'no_waiting' | 'parking';
// What kind of place a parking zone is - drives the wording the rider is shown.
export type ZoneCategory = 'mall' | 'airport' | 'station';

export interface MapZone {
  id: string;
  zone_type: ZoneType;
  name: string;
  advice: string | null;
  kind: 'circle' | 'polygon';
  center_lat: number | null;
  center_lng: number | null;
  radius_m: number | null;
  polygon: { lat: number; lng: number }[] | null;
  parking_fee: number | null;
  category: ZoneCategory | null;
  alt_name: string | null;
  alt_lat: number | null;
  alt_lng: number | null;
}

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';
// v3: v2's contents cannot be trusted - see the note on SOFT_TTL_MS. Changing
// the key abandons every stale copy in the field rather than waiting for it to
// age out.
const CACHE_KEY = 'mapZones.v3';

// How long a cached list is used WITHOUT checking for a newer one. The previous
// version used six hours and returned early inside that window, so a zone added
// at noon could not reach a device until evening - which is exactly what
// happened: riders kept getting the parking prompt at the malls seeded on day
// one and nothing at the ones added afterwards, because their phones had a
// "fresh" list from before those existed.
//
// The premise ("zones change every few weeks") was wrong twice over. Zones do
// change in bursts, and more importantly a device that never asks is a device
// nobody can correct. Five minutes now, and the cache is served INSTANTLY
// regardless - the TTL only decides whether a background refresh runs, never
// whether the rider waits.
const SOFT_TTL_MS = 5 * 60 * 1000;
// Beyond this a cached list is too old to trust offline; better no warning than
// a confidently wrong one.
const HARD_TTL_MS = 7 * 24 * 60 * 60 * 1000;

let zones: MapZone[] = [];
let loadedAt = 0;
let inflight: Promise<MapZone[]> | null = null;
const listeners = new Set<() => void>();

/**
 * Called whenever a background refresh brings in a different list, so a screen
 * showing zone-dependent UI can recompute. Returns an unsubscribe function.
 */
export function onZonesUpdated(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function publish(list: MapZone[]): void {
  zones = list;
  loadedAt = Date.now();
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: loadedAt, list })); } catch {}
  listeners.forEach((fn) => { try { fn(); } catch {} });
}

/** Fetch the current list. Deduplicated - concurrent callers share one request. */
export function refreshMapZones(): Promise<MapZone[]> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // no-store, because this is the request whose whole purpose is to find out
      // whether the list changed. Letting the webview answer it from its own HTTP
      // cache reintroduces the staleness this function exists to fix.
      const res = await fetch(`${API_URL}/zones`, { cache: 'no-store' });
      if (!res.ok) throw new Error(String(res.status));
      const list = await res.json();
      if (Array.isArray(list)) publish(list);
    } catch {
      // Offline, or the backend hasn't been migrated yet. Keep whatever we have
      // (possibly nothing) - a missing warning is a smaller failure than a
      // booking screen that won't load.
    } finally {
      inflight = null;
    }
    return zones;
  })();
  return inflight;
}

/**
 * Zone list: memory → localStorage → network, stale-while-revalidate.
 *
 * Resolves with whatever is already known, immediately, so nothing on screen
 * ever waits on the network; a refresh runs behind it and calls the
 * onZonesUpdated listeners if the answer changed. Only a completely cold start
 * with nothing cached actually awaits the request.
 */
export async function loadMapZones(): Promise<MapZone[]> {
  if (!zones.length) {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const { at, list } = JSON.parse(raw);
        if (Array.isArray(list) && Date.now() - at < HARD_TTL_MS) {
          zones = list;
          loadedAt = at;
        }
      }
    } catch {}
  }

  if (!zones.length) return refreshMapZones();      // nothing to show - must wait
  if (Date.now() - loadedAt > SOFT_TTL_MS) refreshMapZones(); // have something - revalidate behind it
  return zones;
}

// A native app can stay alive for days, so "once at startup" is not a refresh
// policy. Coming back to the foreground is the moment worth re-checking on:
// it's when a rider is about to book, and it costs one small request.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - loadedAt > SOFT_TTL_MS) {
      refreshMapZones();
    }
  });
}

const R_EARTH = 6371000;

function haversineMeters(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R_EARTH * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Ray casting. Zones are a few hundred metres across, so treating lat/lng as a
// flat plane is exact to well under a metre - no projection needed. Longitude
// isn't scaled by cos(lat): the test only asks which side of each edge the point
// falls on, and a uniform horizontal scale can't change that answer.
function pointInPolygon(lat: number, lng: number, ring: { lat: number; lng: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const yi = ring[i].lat, xi = ring[i].lng;
    const yj = ring[j].lat, xj = ring[j].lng;
    const straddles = (yi > lat) !== (yj > lat);
    if (straddles && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export function zoneContains(zone: MapZone, lat: number, lng: number): boolean {
  if (zone.kind === 'circle') {
    if (zone.center_lat == null || zone.center_lng == null || !zone.radius_m) return false;
    return haversineMeters(lat, lng, zone.center_lat, zone.center_lng) <= zone.radius_m;
  }
  const ring = zone.polygon;
  if (!Array.isArray(ring) || ring.length < 3) return false;
  return pointInPolygon(lat, lng, ring);
}

/**
 * The zone of the given type a point falls in, or null. Synchronous, so it can
 * run straight out of a useMemo on every pin move - call loadMapZones() once on
 * mount to fill the cache first.
 *
 * Overlaps are expected: the CBD core polygon sits underneath all the CBD street
 * circles, and Prestige Plaza is both a mall and a no-waiting stretch of Ngong
 * Road. Circles are checked first because they're the tighter, more specific
 * shape, so a rider on Tom Mboya Street gets that street's advice rather than
 * the generic CBD wording.
 */
export function findZone(type: ZoneType, lat?: number | null, lng?: number | null): MapZone | null {
  if (lat == null || lng == null || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (!zones.length) return null;
  const of = zones.filter((z) => z.zone_type === type);
  return (
    of.find((z) => z.kind === 'circle' && zoneContains(z, lat, lng)) ??
    of.find((z) => z.kind === 'polygon' && zoneContains(z, lat, lng)) ??
    null
  );
}

export const findNoWaitingZone = (lat?: number | null, lng?: number | null) =>
  findZone('no_waiting', lat, lng);

/**
 * The mall whose car park a drop-off here sits in, or null.
 *
 * A zone with no fee is treated as no zone at all - a free car park is "not a
 * parking zone", not "a parking zone charging nothing", and the latter would put
 * a pointless KES 0 choice in front of the rider.
 */
export function findParkingZone(lat?: number | null, lng?: number | null): MapZone | null {
  const zone = findZone('parking', lat, lng);
  return zone && zone.parking_fee && zone.parking_fee > 0 ? zone : null;
}
